import { StoreonModule } from "storeon";
import { BehalfOfRelation } from "./enums/BehalfOfRelation";
import { IBroker, IPediatricDental, IQualifyingEvent } from "./interfaces";

export interface IEligibilityStore {
  eligibilityStore: IEligibilityStoreShape;
}

export interface IEligibilityStoreShape {
    qualifyingLifeEvent: IQualifyingEvent;
    broker: IBroker;
    pediatricDental: IPediatricDental;
    relationToSubscriber: BehalfOfRelation | undefined;
    isBetween26to29: boolean | undefined;
    isUnder19: boolean | undefined;
    needsPediatricDental: boolean | undefined;
    isEnrollingOnBehalf: boolean | undefined;
    effectiveStartDate?: string | undefined;
  }

export interface IEligibilityEvents {
  setDoB: Date;
  updateQLEEvent: any;
  updateQLEDate: Date;

  setBroker: IBroker;
  setBrokerFirstName: string;
  setBrokerLastName: string;
  setBrokerEmail: string;
  setBrokerAgencyName: string;
  setBrokerMVPAgencyNumber: string;

  setPediatricDental: IPediatricDental;
  setPDCompany: string;
  setPDPolicyNumber: string;
  togglePDSDidUserVerify: undefined;

  setRelationToSubscriber: BehalfOfRelation;
  toggleHasSpouse: undefined;
  toggleHasDependents: undefined;

  setIsBetween26to29: boolean;
  setIsUnder19: boolean;
  toggleNeedsPediatricDental: boolean;
  setIsEnrollingOnBehalf: boolean;
  setEffectiveStartDate: string;
}

const eligibilityStore: StoreonModule<
  IEligibilityStore,
  IEligibilityEvents
> = (store) => {
  store.on("@init", initializeEligibility);
  store.on("updateQLEEvent", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      qualifyingLifeEvent: {
        ...state.eligibilityStore.qualifyingLifeEvent,
        eventType: event.eventType,
        label: event.eventLabel,
      },
    },
  }));
  store.on("updateQLEDate", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      qualifyingLifeEvent: {
        ...state.eligibilityStore.qualifyingLifeEvent,
        date: event,
      },
    },
  }));

  store.on("setBroker", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: event,
    },
  }));
  store.on("setBrokerFirstName", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: { ...state.eligibilityStore.broker, firstName: event },
    },
  }));
  store.on("setBrokerLastName", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: { ...state.eligibilityStore.broker, lastName: event },
    },
  }));
  store.on("setBrokerEmail", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: { ...state.eligibilityStore.broker, email: event },
    },
  }));
  store.on("setBrokerAgencyName", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: { ...state.eligibilityStore.broker, agencyName: event },
    },
  }));
  store.on("setBrokerMVPAgencyNumber", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      broker: { ...state.eligibilityStore.broker, mvpAgencyNumber: event },
    },
  }));

  store.on("setPediatricDental", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      pediatricDental: event,
    },
  }));
  store.on("setPDCompany", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      pediatricDental: {
        ...state.eligibilityStore.pediatricDental,
        company: event,
      },
    },
  }));
  store.on("setPDPolicyNumber", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      pediatricDental: {
        ...state.eligibilityStore.pediatricDental,
        policyNumber: event,
      },
    },
  }));
  store.on("togglePDSDidUserVerify", (state) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      pediatricDental: {
        ...state.eligibilityStore.pediatricDental,
        didUserVerify: !state.eligibilityStore.pediatricDental.didUserVerify,
      },
    },
  }));

  store.on("setRelationToSubscriber", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      relationToSubscriber: event,
    },
  }));

  store.on("setIsBetween26to29", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      isBetween26to29: event,
    },
  }));
  store.on("setIsEnrollingOnBehalf", (state, event) => {
    if (state.eligibilityStore.isEnrollingOnBehalf) {
      return {
        ...state,
        eligibilityStore: {
          ...state.eligibilityStore,
          broker: {
            firstName: "",
            lastName: "",
            email: "",
            agencyName: "",
            mvpAgencyNumber: "",
          },
          relationToSubscriber: undefined,
          isEnrollingOnBehalf: event,
        },
      };
    }
    return {
      ...state,
      eligibilityStore: {
        ...state.eligibilityStore,
        isEnrollingOnBehalf: event,
      },
    };
  });
  store.on("setIsUnder19", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      isUnder19: event,
    },
  }));
  store.on("toggleNeedsPediatricDental", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      needsPediatricDental: event,
    },
  }));
  store.on("setEffectiveStartDate", (state, event) => ({
    ...state,
    eligibilityStore: {
      ...state.eligibilityStore,
      effectiveStartDate: event,
    },
  }));
};

const initializeEligibility = (): IEligibilityStore => ({
  eligibilityStore: {
    qualifyingLifeEvent: {
      eventType: undefined,
      label: undefined,
      date: undefined,
    },
    broker: {
      firstName: "",
      lastName: "",
      email: "",
      agencyName: "",
      mvpAgencyNumber: "",
    },
    pediatricDental: {
      company: "",
      policyNumber: "",
      didUserVerify: false,
    },
    relationToSubscriber: undefined,
    isBetween26to29: undefined,
    isUnder19: undefined,
    needsPediatricDental: undefined,
    isEnrollingOnBehalf: undefined,
    effectiveStartDate: undefined,
  },
});

export default eligibilityStore;
